import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { PortalShell, Empty, Stat } from "../components/ProtectedRoute";
import { StatusBadge, StatusTimeline } from "../components/StatusTimeline";
import { CountryMultiSelect } from "../components/CountryMultiSelect";
import { useCountry } from "../context/CountryContext";
import { api, errMsg, money, fmtDate, TECH, STATUS_LABEL } from "../lib/api";

const TABS = [["overview", "Overview"], ["packages", "Packages"], ["coverage", "Coverage areas"], ["orders", "Orders"], ["profile", "Profile"]];
const NEXT = { pending_supplier: ["accepted", "rejected"], accepted: ["installation_scheduled"], installation_scheduled: ["active"] };
const BLANK = { country: "", type: "fibre", name: "", network: "", download_mbps: 50, upload_mbps: 50, data_cap: "Uncapped", wholesale_price: "", setup_fee: 0 };

const ccyOf = (countries, code) => countries.find((c) => c.code === code)?.currency || "USD";

const PkgDialog = ({ open, initial, supplier, onClose, onSaved }) => {
  const [f, setF] = useState(initial || BLANK);
  useEffect(() => setF(initial || { ...BLANK, country: supplier?.countries[0] || "" }), [initial, open, supplier]);
  const { countries } = useCountry();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    const body = { ...f, download_mbps: +f.download_mbps, upload_mbps: +f.upload_mbps, wholesale_price: +f.wholesale_price, setup_fee: +f.setup_fee || 0 };
    try {
      if (initial?.id) await api.put(`/supplier/packages/${initial.id}`, body);
      else await api.post("/supplier/packages", body);
      toast.success("Package submitted for HostAfricair approval");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="supplier-package-builder">
        <DialogHeader><DialogTitle>{initial?.id ? "Edit package" : "New package"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-3">
          <div><Label>Country</Label>
            <Select value={f.country} onValueChange={(v) => setF({ ...f, country: v })}>
              <SelectTrigger className="mt-1" data-testid="pkg-country-select"><SelectValue placeholder="Country" /></SelectTrigger>
              <SelectContent>{supplier?.countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Technology</Label>
            <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
              <SelectTrigger className="mt-1" data-testid="pkg-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(TECH).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="col-span-2"><Label>Package name</Label><Input className="mt-1" required value={f.name} onChange={set("name")} data-testid="pkg-name-input" /></div>
          <div><Label>Network</Label><Input className="mt-1" required value={f.network} onChange={set("network")} data-testid="pkg-network-input" /></div>
          <div><Label>Data</Label><Input className="mt-1" required value={f.data_cap} onChange={set("data_cap")} data-testid="pkg-data-input" /></div>
          <div><Label>Down (Mbps)</Label><Input className="mt-1" type="number" min={1} required value={f.download_mbps} onChange={set("download_mbps")} data-testid="pkg-down-input" /></div>
          <div><Label>Up (Mbps)</Label><Input className="mt-1" type="number" min={1} required value={f.upload_mbps} onChange={set("upload_mbps")} data-testid="pkg-up-input" /></div>
          <div className="col-span-2"><Label>Wholesale price / month ({ccyOf(countries, f.country)})</Label><Input className="mt-1" type="number" min={1} step="any" required value={f.wholesale_price} onChange={set("wholesale_price")} data-testid="pkg-wholesale-input" /></div>
          <Button className="col-span-2" data-testid="pkg-save-btn">Submit for approval</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const CoverageTab = ({ supplier }) => {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ country: supplier.countries[0], area: "", technologies: ["fibre"] });
  const load = useCallback(() => api.get("/supplier/coverage").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/supplier/coverage", f);
      setF({ ...f, area: "" });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const toggle = (t) => setF({ ...f, technologies: f.technologies.includes(t) ? f.technologies.filter((x) => x !== t) : [...f.technologies, t] });
  return (
    <div className="grid gap-6 lg:grid-cols-3" data-testid="supplier-coverage-manager">
      <form onSubmit={add} className="space-y-3 rounded-lg border bg-white p-5">
        <p className="font-semibold">Add coverage area</p>
        <p className="text-xs text-muted-foreground">Suburb or city names. Use * for nationwide (e.g. LTE).</p>
        <Select value={f.country} onValueChange={(v) => setF({ ...f, country: v })}>
          <SelectTrigger data-testid="coverage-country-select"><SelectValue /></SelectTrigger>
          <SelectContent>{supplier.countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Input required placeholder="e.g. Sandton" value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })} data-testid="coverage-area-input" />
        <div className="flex gap-2">{Object.entries(TECH).map(([k, v]) => (
          <button type="button" key={k} onClick={() => toggle(k)} data-testid={`coverage-tech-${k}`}
            className={`rounded border px-2.5 py-1 text-xs font-semibold ${f.technologies.includes(k) ? "border-primary bg-accent text-primary" : "text-muted-foreground"}`}>{v.label}</button>))}
        </div>
        <Button className="w-full" data-testid="coverage-add-btn"><Plus className="mr-1 h-4 w-4" />Add area</Button>
      </form>
      <div className="lg:col-span-2">{rows.length ? (
        <div className="rounded-lg border bg-white">{rows.map((a) => (
          <div key={a.id} className="flex items-center gap-3 border-b p-3 last:border-0" data-testid={`coverage-row-${a.id}`}>
            <span className="font-mono-data text-xs text-muted-foreground">{a.country}</span>
            <span className="flex-1 font-medium">{a.area === "*" ? "Nationwide" : a.area}</span>
            <span className="text-xs">{a.technologies.map((t) => TECH[t]?.label).join(", ")}</span>
            <Button size="icon" variant="ghost" data-testid={`coverage-delete-${a.id}`} onClick={async () => { await api.delete(`/supplier/coverage/${a.id}`); load(); }}><Trash2 className="h-4 w-4" /></Button>
          </div>))}
        </div>) : <Empty>No coverage areas yet.</Empty>}
      </div>
    </div>
  );
};

const OrdersTab = () => {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/supplier/orders").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const move = async (id, status) => {
    try {
      await api.patch(`/supplier/orders/${id}`, { status });
      toast.success(`Order moved to ${STATUS_LABEL[status]}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!rows) return <Loader2 className="h-5 w-5 animate-spin" />;
  if (!rows.length) return <Empty>No orders yet. They'll appear here as soon as customers pay.</Empty>;
  return (
    <div className="space-y-4" data-testid="supplier-orders-table">
      {rows.map((o) => (
        <div key={o.id} className="rounded-lg border bg-white p-5" data-testid={`supplier-order-${o.id}`}>
          <div className="flex flex-wrap items-start gap-3">
            <div className="flex-1">
              <p className="font-semibold">{o.name} · {TECH[o.type]?.label} <span className="font-mono-data text-xs text-muted-foreground">#{o.order_number}</span></p>
              <p className="text-sm text-muted-foreground">{o.user_name} · {o.phone || "no phone"} · {o.install_address}</p>
              <p className="text-xs text-muted-foreground">Wholesale {money(o.wholesale_price, o.currency)}/mo · {fmtDate(o.created_at)}</p>
            </div>
            <div className="flex gap-2">{(NEXT[o.status] || []).map((s) => (
              <Button key={s} size="sm" variant={s === "rejected" ? "outline" : "default"} onClick={() => move(o.id, s)} data-testid={`supplier-order-${s}-btn-${o.id}`}>{STATUS_LABEL[s]}</Button>))}
            </div>
          </div>
          <div className="mt-4"><StatusTimeline status={o.status} /></div>
        </div>
      ))}
    </div>
  );
};

const ProfileTab = ({ supplier, onSaved }) => {
  const [f, setF] = useState({ name: supplier.name, countries: supplier.countries, website: supplier.website || "", contact_email: supplier.contact_email || "" });
  const save = async () => {
    try {
      await api.put("/supplier/me", f);
      toast.success("Profile saved");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="max-w-2xl space-y-4 rounded-lg border bg-white p-6" data-testid="supplier-profile-form">
      <div><Label>Company name</Label><Input className="mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="profile-name-input" /></div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div><Label>Contact email</Label><Input className="mt-1" value={f.contact_email} onChange={(e) => setF({ ...f, contact_email: e.target.value })} /></div>
        <div><Label>Website</Label><Input className="mt-1" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} /></div>
      </div>
      <div><Label>Countries served</Label><div className="mt-1"><CountryMultiSelect value={f.countries} onChange={(countries) => setF({ ...f, countries })} /></div></div>
      <p className="text-xs text-muted-foreground">Integration: <span className="font-semibold">{supplier.integration === "api" ? "API connection" : "Supplier portal"}</span></p>
      <Button onClick={save} data-testid="profile-save-btn">Save profile</Button>
    </div>
  );
};

export default function SupplierPortal() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "overview";
  const { countries } = useCountry();
  const [sum, setSum] = useState(null);
  const [pkgs, setPkgs] = useState([]);
  const [dlg, setDlg] = useState({ open: false, initial: null });
  const load = useCallback(() => {
    api.get("/supplier/summary").then((r) => setSum(r.data)).catch(() => {});
    api.get("/supplier/packages").then((r) => setPkgs(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!sum) return <div className="p-10"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  const s = sum.supplier;
  return (
    <PortalShell testid="supplier-portal-dashboard" title={s.name} subtitle={`Supplier portal · ${s.countries.join(", ")}`} tabs={TABS} active={tab} onTab={(t) => setSp({ tab: t })}>
      {s.status !== "approved" && (
        <div className="mb-6 flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="supplier-pending-banner">
          <Clock className="h-4 w-4" /> Your supplier account is <b>{STATUS_LABEL[s.status]}</b>. Add packages now; they go live once HostAfricair approves you.
        </div>
      )}
      {tab === "overview" && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Live packages" value={sum.packages_live} testid="stat-packages-live" />
          <Stat label="Awaiting approval" value={sum.packages_pending} testid="stat-packages-pending" />
          <Stat label="Total orders" value={sum.orders_total} testid="stat-orders-total" />
          <Stat label="New orders" value={sum.by_status.pending_supplier || 0} testid="stat-orders-new" tone="text-primary" />
          <div className="rounded-lg border bg-white p-5 sm:col-span-2 lg:col-span-4" data-testid="stat-monthly-wholesale">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Monthly wholesale revenue (active services)</p>
            <p className="mt-2 font-display text-2xl font-bold">{Object.entries(sum.monthly_wholesale).map(([c, v]) => money(v, c)).join(" · ") || "—"}</p>
          </div>
        </div>
      )}
      {tab === "packages" && (
        <div>
          <Button className="mb-4" onClick={() => setDlg({ open: true, initial: null })} data-testid="new-package-btn"><Plus className="mr-1 h-4 w-4" />New package</Button>
          {pkgs.length ? (
            <div className="overflow-x-auto rounded-lg border bg-white">
              <Table data-testid="supplier-packages-table">
                <TableHeader><TableRow><TableHead>Package</TableHead><TableHead>Country</TableHead><TableHead>Speed</TableHead><TableHead>Wholesale</TableHead><TableHead>Retail</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
                <TableBody>{pkgs.map((p) => (
                  <TableRow key={p.id} data-testid={`supplier-pkg-row-${p.id}`}>
                    <TableCell className="font-medium">{p.name} <span className="text-xs text-muted-foreground">· {TECH[p.type]?.label} · {p.network}</span></TableCell>
                    <TableCell>{p.country}</TableCell>
                    <TableCell className="font-mono-data text-xs">{p.download_mbps}/{p.upload_mbps} · {p.data_cap}</TableCell>
                    <TableCell>{money(p.wholesale_price, ccyOf(countries, p.country))}</TableCell>
                    <TableCell>{p.retail_price ? money(p.retail_price, ccyOf(countries, p.country)) : "—"}</TableCell>
                    <TableCell><StatusBadge status={p.status} /></TableCell>
                    <TableCell className="whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => setDlg({ open: true, initial: p })} data-testid={`supplier-pkg-edit-${p.id}`}>Edit</Button>
                      <Button size="icon" variant="ghost" data-testid={`supplier-pkg-delete-${p.id}`} onClick={async () => { await api.delete(`/supplier/packages/${p.id}`); load(); }}><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>))}
                </TableBody>
              </Table>
            </div>) : <Empty>No packages yet.</Empty>}
          <PkgDialog open={dlg.open} initial={dlg.initial} supplier={s} onClose={() => setDlg({ open: false, initial: null })} onSaved={() => { setDlg({ open: false, initial: null }); load(); }} />
        </div>
      )}
      {tab === "coverage" && <CoverageTab supplier={s} />}
      {tab === "orders" && <OrdersTab />}
      {tab === "profile" && <ProfileTab supplier={s} onSaved={load} />}
    </PortalShell>
  );
}
